import React, { useState, useEffect } from 'react';
import type { MediaPreviewProps } from './types';

const MediaPreview: React.FC<MediaPreviewProps> = ({
  imageUrl,
  title,
  deviceType,
  bannerId,
  onImageError,
  onImageClick,
}) => {
  const [mediaStatus, setMediaStatus] = useState<'loading' | 'loaded' | 'error'>('loading');
  const [errorReason, setErrorReason] = useState<string>('');

  // Check if the URL is a video file
  const isVideo = imageUrl?.toLowerCase().endsWith('.mp4') || false;

  // Reset when imageUrl changes
  useEffect(() => {
    if (imageUrl) {
      setMediaStatus('loading');
      setErrorReason('');
    }
  }, [imageUrl]);

  const handleMediaError = (event: React.SyntheticEvent<HTMLImageElement | HTMLVideoElement>) => {
    const media = event.currentTarget;
    let reason = 'Unknown error';

    if (media.src.includes('banners.skin-seoul.com')) {
      reason = `Server error - ${isVideo ? 'video' : 'image'} cannot be loaded`;
    } else if (!media.src) {
      reason = `No ${isVideo ? 'video' : 'image'} URL provided`;
    } else {
      reason = `${isVideo ? 'Video' : 'Image'} failed to load`;
    }

    console.error(`Banner ${isVideo ? 'video' : 'image'} error: ${reason} - URL: ${media.src}`);
    setMediaStatus('error');
    setErrorReason(reason);
    onImageError(bannerId);
  };

  const handleMediaLoad = () => {
    setMediaStatus('loaded');
  };

  // If no media URL, show no media message
  if (!imageUrl) {
    return (
      <div className="text-center">
        <div
          style={{ width: '100px', height: '60px' }}
          className="bg-light d-flex align-items-center justify-content-center rounded"
        >
          <div className="text-center">
            <div className="small text-muted">No media</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="media-preview-container"
      onClick={() => onImageClick(imageUrl, title, deviceType)}
      style={{ cursor: 'pointer' }}
    >
      <div style={{ width: '100px', height: '60px', position: 'relative', overflow: 'hidden' }}>
        {mediaStatus === 'error' ? (
          // Show error reason instead of generic error
          <div
            className="bg-warning text-dark rounded d-flex align-items-center justify-content-center"
            style={{
              width: '100%',
              height: '100%',
              fontSize: '0.7rem',
              textAlign: 'center',
              padding: '2px',
            }}
            title={`Error: ${errorReason} - URL: ${imageUrl}`}
          >
            <div>
              <i className="bi bi-exclamation-triangle"></i>
              <div style={{ fontSize: '0.6rem' }}>{errorReason}</div>
            </div>
          </div>
        ) : isVideo ? (
          // Show video with poster frame
          <video
            src={imageUrl}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              position: 'absolute',
              top: 0,
              left: 0,
            }}
            className="rounded"
            title="Click to preview video"
            onError={handleMediaError}
            onLoadedData={handleMediaLoad}
            muted
            preload="metadata"
          />
        ) : (
          // Show the actual image
          <img
            src={imageUrl}
            alt={`${title} (${deviceType})`}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              position: 'absolute',
              top: 0,
              left: 0,
            }}
            className="rounded"
            title="Click to preview"
            onError={handleMediaError}
            onLoad={handleMediaLoad}
            loading="lazy"
          />
        )}
      </div>
    </div>
  );
};

// Remove the generic MediaErrorOverlay - not needed anymore
export const MediaErrorOverlay: React.FC = () => null;

export default MediaPreview;
